import { CSSProperties, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { GrButton, GrDialog, GrDialogProps, GrIcon, GrIconButton, GrSelect, GrStack, GrTextField, GrTypography } from "@jnpmedi/grebob";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { convertFileExtToGrIconType } from "@newLibs/converting";
import { isEmailWithoutUTF8LocalPart, MvnMessage } from "@newLibs/utils";
import { UseBoolean } from "@newHooks/common/useBoolean";
import { notify } from "@newHooks/context/snackbar/notify";
import { useCreateFolderSharingsExternalMutation } from "@newHooks/mutation/folderSharing/createFolderSharingsExternal.mutation";
import { useRemoveFolderSharingMutation } from "@newHooks/mutation/folderSharing/removeFolderSharing.mutation";
import { useUpsertFolderSharingsMutation } from "@newHooks/mutation/folderSharing/upsertFolderSharings.mutation";
import { FolderKey } from "@newHooks/query/folder/key";
import { useFolderSharingsQuery } from "@newHooks/query/folderSharing/folderSharings.query";
import { useFolderSharingLinkQuery } from "@newHooks/query/folderSharingLink/folderSharingLink.query";
import { ellipsis } from "@assets/styles/layout";
import { AdvancedSharingSettingsDialog } from "./AdvancedSharingSettingsDialog";
import { SharingRoleGuard } from "./SharingRoleGuardView";
import { DocsTable } from "../DocsTable/DocsTable";
import { DocsTableRefProps } from "../DocsTable/DocsTableContents";
import { DOCS_FOLDER_CODE } from "@maven-msg/code/docs/folder";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { trimAndToLowerCase } from "@maven-rest/docs/DocumentSign.conv";
import { BrowseEntrySummary, BrowseEntryType, FolderType } from "@maven-rest/docs/Folder.dto";
import { ReadableFolderSharingRole, ReadableFolderSharingType } from "@maven-rest/docs/FolderSharing.conv";
import {
  FolderSharing,
  FolderSharingPayload,
  FolderSharingRole,
  FolderSharingToMember,
  FolderSharingType,
} from "@maven-rest/docs/FolderSharing.dto";
import { compareBigintSafe } from "@maven-helper/functions/Misc.function";

export type SharingTargetType = { folderId: bigint; fileId?: bigint; fileName: string; browseEntrySummary?: BrowseEntrySummary };

interface SharingSettingsDialogDialogProps {
  open: UseBoolean;
  sharingTarget: SharingTargetType;
  organizationId: bigint;
  folderType: FolderType;
  size?: GrDialogProps["size"];
}

const DEFAULT_SHARING_ARGS = { email: "", sharingId: BigInt(-1) };
export function SharingSettingsDialog({
  open,
  sharingTarget,
  organizationId,
  folderType,
  size = "small",
}: SharingSettingsDialogDialogProps) {
  const queryClient = useQueryClient();
  const { folderId, fileId, fileName } = sharingTarget;

  const folderSharingTableRef = useRef<DocsTableRefProps>(null);

  const [email, setEmail] = useState<string>("");
  const [settingsDialogVisibility, setSettingsDialogVisibility] = useState<boolean>(false);
  const [requestPayload, setRequestPayload] = useState<FolderSharingPayload[]>([]);
  const [folderSharingList, setFolderSharingList] = useState<FolderSharing[]>([]);
  const [removeSharingSettingDialogOpen, setRemoveSharingSettingDialogOpen] = useState<boolean>(false);

  const sharingMemberNameRef = useRef<{ email: string; sharingId: bigint }>({ ...DEFAULT_SHARING_ARGS });

  const isInValidEmail = !email || !isEmailWithoutUTF8LocalPart(email);
  const isNotEConsentFile: boolean = folderType !== FolderType.ECONSENT;

  const columns: ColumnDef<FolderSharing, keyof FolderSharing>[] = [
    {
      accessorKey: "email",
      size: 226,
      header: () => renderTableHeaderTitle("Email"),
      cell: ({ row }) => (
        <GrTypography type="Content.Body04" color="grey090">
          {getSharingUserEmail(row.original)}
        </GrTypography>
      ),
    },
    {
      accessorKey: "type",
      size: 116,
      header: () => renderTableHeaderTitle("Type"),
      cell: ({ row }) => {
        const originRow = row.original;
        return (
          <GrStack flexDirection="column">
            {ReadableFolderSharingType(originRow.sharingType)}
            {!!originRow.isInherited && (
              <GrTypography type="Content.Caption04" color="grey050">
                (Inherited)
              </GrTypography>
            )}
          </GrStack>
        );
      },
    },
    {
      accessorKey: "role",
      size: 206,
      header: () => renderTableHeaderTitle("Role"),
      cell: ({ row }) => {
        const originRow = row.original;
        const grSelectStyle: CSSProperties = { width: "140px" };
        const isMemberSharing = originRow.sharingType === FolderSharingType.Member;
        // NOTE: external sharing의 경우. overridden role (하위 depth의 권한설정이 먼저된 이후 상위 depth의 권한설정이 나중에 설정된 경우) 는 별도의 공유 건으로 판단.
        const isExternalMemberSharingDeletable = !isMemberSharing && !originRow.isInherited;
        return (
          <GrStack gap={2} width="100%" justifyContent="space-between">
            {originRow.sharingRole === FolderSharingRole.Owner ? (
              <GrSelect
                size="medium"
                multiple={false}
                style={grSelectStyle}
                disabled
                options={[
                  { label: ReadableFolderSharingRole({ role: FolderSharingRole.Owner, folderType }), value: FolderSharingRole.Owner },
                ]}
                value={FolderSharingRole.Owner}
              />
            ) : (
              <GrStack flexDirection="column">
                <GrSelect
                  multiple={false}
                  size="medium"
                  style={grSelectStyle}
                  disabled={originRow.isInherited}
                  options={makeSharingSettingsRoleSelectionOption(originRow, folderType)}
                  onChange={(_, val) => {
                    if (originRow.id) {
                      setRequestPayload(
                        requestPayload
                          .filter((e) => !("sharingId" in e && compareBigintSafe(e.sharingId, originRow.id)))
                          .concat(originRow.sharingRole === val ? [] : [{ sharingId: originRow.id, sharingRole: val as FolderSharingRole }])
                      );
                    } else if (isMemberSharing) {
                      setRequestPayload(
                        requestPayload
                          .filter((e) => !("userId" in e && compareBigintSafe(e.userId, (originRow as FolderSharingToMember).user?.id)))
                          .concat(
                            originRow.sharingRole === val ? [] : [{ userId: originRow.user.id, sharingRole: val as FolderSharingRole }]
                          )
                      );
                    }
                  }}
                  value={
                    requestPayload.find(
                      (e) =>
                        (isMemberSharing && "userId" in e && e.userId && compareBigintSafe(e.userId, originRow.user?.id)) ||
                        ("sharingId" in e && e.sharingId && compareBigintSafe(e.sharingId, originRow.id))
                    )?.sharingRole || originRow.sharingRole
                  }
                />
                {!!originRow.overriddenRole && (
                  <GrTypography type="Content.Caption04" color="grey050">
                    Overridden: {ReadableFolderSharingRole({ role: originRow.overriddenRole, folderType })}
                  </GrTypography>
                )}
              </GrStack>
            )}
            {isExternalMemberSharingDeletable && (
              <GrIconButton
                iconType="trash"
                size={16}
                onClick={() => {
                  setRemoveSharingSettingDialogOpen(true);
                  const email = originRow.email;
                  const sharingId = originRow.id;
                  sharingMemberNameRef.current = { email, sharingId };
                }}
              />
            )}
          </GrStack>
        );
      },
    },
  ];

  useEffect(() => {
    setRequestPayload([]);
    return () => handleCloseSharingSettingDialog();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { refetch: refetchFolderSharingLink } = useFolderSharingLinkQuery(
    { organizationId, folderId, fileId },
    {
      enabled: false,
      onSuccess: (data) => {
        navigator?.clipboard
          ?.write([
            new ClipboardItem({
              "text/plain": new Blob([`${process.env.REACT_APP_DOCS_FE_URL}/sharings/${data.sharingLink.token}`], {
                type: "text/plain",
              }),
            }),
          ])
          ?.then(() => {
            notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_EXTERNAL_URL_COPY_SUCC), "SUCC");
          })
          .catch((e) => {
            notify(
              MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_EXTERNAL_URL_COPY_FAIL, { message: e.message || e.responseMessage || "" }),
              "ERROR"
            );
          });
      },
    }
  );

  const { mutate: createFolderSharingExternalRequest, isLoading: isCreateFolderSharingExternalLoading } =
    useCreateFolderSharingsExternalMutation({
      onSuccess: () => {
        setEmail("");
        refetchFolderSharings();

        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_EXTERNAL_EMAIL_ADD_SUCC), "SUCC");
      },
      onError: (error) =>
        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_EXTERNAL_EMAIL_ADD_FAIL, { message: error.responseMessage }), "ERROR"),
    });

  const { mutate: upsertFolderSharingsRequest } = useUpsertFolderSharingsMutation({
    onSuccess: () => {
      open.onFalse();
      handleCloseSharingSettingDialog();

      queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFolders] });

      notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_UPDATE_SUCC), "SUCC");
    },
    onError: (error) =>
      notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_UPDATE_FAIL, { message: error.responseMessage }), "ERROR"),
  });

  const { refetch: refetchFolderSharings, isInitialLoading: isFolderSharingsInitialLoading } = useFolderSharingsQuery(
    { organizationId, folderId, fileId },
    {
      enabled: open.value && !!organizationId && (!!folderId || !!fileId),
      onSuccess: ({ sharings }) => {
        if (sharings) {
          setFolderSharingList(sharings);
        }
      },
    }
  );

  const { mutate: removeFolderSharingRequest } = useRemoveFolderSharingMutation({
    onSuccess: () => {
      refetchFolderSharings();
      notify(MvnMessage("Successfully deleted."), "SUCC");
      setRemoveSharingSettingDialogOpen(false);
    },
  });

  const handleEmailTextFieldKeydown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    if (evt.key !== "Enter") {
      return;
    }
    handleFolderSharingRequest();
  };

  const handleFolderSharingRequest = () => {
    if (isInValidEmail) {
      notify(MvnMessage(DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_EMAIL_INVALID, { email: email || "EMPTY" }), "WARN");
      return;
    }
    createFolderSharingExternalRequest({ organizationId, folderId, fileId, email });
  };

  const handleCloseSharingSettingDialog = () => {
    setEmail("");
    setRequestPayload([]);
    setSettingsDialogVisibility(false);
    setFolderSharingList([]);
    setRemoveSharingSettingDialogOpen(false);
    sharingMemberNameRef.current = { ...DEFAULT_SHARING_ARGS };
  };

  const handleUpsertFolderSharingsRequest = () => {
    upsertFolderSharingsRequest({ organizationId, folderId, fileId, sharings: requestPayload });
  };

  return (
    <GrDialog
      header={
        <GrStack justifyContent="space-between">
          <GrTypography type="Content.Title02" color="grey080">
            {MvnMessage(DOCS_FOLDER_LANG.DOCS_SHARING_SETTING_DIALOG_HEADER)}
          </GrTypography>

          {isNotEConsentFile && (
            <GrButton startIcon="link" variant="text" size="medium" onClick={() => refetchFolderSharingLink()}>
              <GrTypography type="Content.Subtitle03">{MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_COPY_LINK_BTN)}</GrTypography>
            </GrButton>
          )}
        </GrStack>
      }
      footer={
        isNotEConsentFile && (
          <GrButton color="grey" variant="outlined" startIcon="setting" size="medium" onClick={() => setSettingsDialogVisibility(true)}>
            {MvnMessage(DOCS_FOLDER_LANG.DOCS_ADVANCED_SHARING_SETTING_DIALOG_HEADER)}
          </GrButton>
        )
      }
      open={open.value}
      size={size}
      onConfirm={handleUpsertFolderSharingsRequest}
      onClose={open.onFalse}
      preventCloseOnOutsideClick
      confirmButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_SAVE_NOUN), disabled: requestPayload.length === 0 }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
    >
      <>
        <SharingRoleGuard folderId={folderId} fileId={fileId} role={FolderSharingRole.Manager}>
          {(isAuthorized) => (
            <GrStack flexDirection="column" gap={8}>
              <GrStack style={{ marginBottom: "16px" }} alignItems="center">
                <GrTypography type="Content.Subtitle04" color="grey090">
                  {`${
                    sharingTarget.browseEntrySummary?.type === BrowseEntryType.Folder
                      ? MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_FOLDER_DESCRIPTION_TITLE)
                      : MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_FILE_DESCRIPTION_TITLE)
                  } :`}
                </GrTypography>
                <GrIcon style={{ margin: "0px 4px" }} size={16} type={convertFileExtToGrIconType(sharingTarget.browseEntrySummary)} />
                <GrTypography type="Content.Subtitle03" color="grey070" style={{ ...ellipsis, maxWidth: "76%" }} title={fileName}>
                  {fileName}
                </GrTypography>
              </GrStack>
              {isNotEConsentFile && (
                <GrStack gap={10}>
                  <GrTextField
                    type="email"
                    autoFocus
                    fullWidth
                    value={email}
                    placeholder="Enter email"
                    onKeyDown={handleEmailTextFieldKeydown}
                    onChange={(evt) => setEmail(trimAndToLowerCase(evt.target.value))}
                  />
                  <GrButton
                    variant="outlined"
                    disabled={isInValidEmail}
                    onClick={handleFolderSharingRequest}
                    isLoading={isCreateFolderSharingExternalLoading}
                  >
                    {isCreateFolderSharingExternalLoading ? "" : "Share"}
                  </GrButton>
                </GrStack>
              )}

              <FolderSharingTableWrapper>
                <DocsTable>
                  <DocsTable.Contents<FolderSharing>
                    ref={folderSharingTableRef}
                    columns={columns}
                    data={folderSharingList}
                    isDataLoading={isFolderSharingsInitialLoading}
                    isDataRefetching={isFolderSharingsInitialLoading}
                  />
                </DocsTable>
              </FolderSharingTableWrapper>
            </GrStack>
          )}
        </SharingRoleGuard>
        {/* IP Address, Watermark Setting Dialog */}
        {isNotEConsentFile && settingsDialogVisibility && (
          <AdvancedSharingSettingsDialog
            visibility={settingsDialogVisibility}
            setVisibility={setSettingsDialogVisibility}
            organizationId={organizationId}
            folderId={folderId}
            fileId={fileId}
          />
        )}
        {/* remove external Sharing member sharing dialog */}
        {removeSharingSettingDialogOpen && (
          <GrDialog
            title={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_DELETE_HEADER)}
            open={removeSharingSettingDialogOpen}
            onClose={() => setRemoveSharingSettingDialogOpen(false)}
            titleIcon={<GrIcon type="alert-hexagon-filled" color="negative-primary" size={24} />}
            size="xs"
            preventCloseOnOutsideClick
            confirmButton={{ children: MvnMessage(COMMON_LANG.DELETE), color: "red" }}
            cancelButton={{ children: MvnMessage(COMMON_LANG.CANCEL) }}
            onConfirm={() => removeFolderSharingRequest({ sharingId: sharingMemberNameRef.current.sharingId })}
          >
            <GrStack flexDirection="column" gap={16}>
              <GrTypography type="Content.Body03" color="grey070">
                {MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_EXTERNAL_EMAIL_DELETE_CONFIRM)}
              </GrTypography>
              <GrTypography type="Content.Subtitle03" color="grey070">
                {sharingMemberNameRef.current.email}
              </GrTypography>
            </GrStack>
          </GrDialog>
        )}
      </>
    </GrDialog>
  );
}

function getSharingUserEmail(folderSharing: FolderSharing): string {
  return folderSharing.sharingType === FolderSharingType.Member
    ? `${folderSharing.user.email}${folderSharing.user.name ? ` (${folderSharing.user.name})` : ""}`
    : folderSharing.email;
}

const renderTableHeaderTitle = (title: string) => {
  return (
    <GrTypography type="Content.Subtitle04" color="grey090">
      {title}
    </GrTypography>
  );
};

function makeSharingSettingsRoleSelectionOption(sharing: FolderSharing, folderType: FolderType) {
  return Object.values(FolderSharingRole)
    .filter((role) => {
      if (role === FolderSharingRole.Owner || (sharing.sharingRole !== FolderSharingRole.Admin && role === FolderSharingRole.Admin)) {
        return false;
      }

      if (folderType === FolderType.ARCHIVER) {
        return [
          FolderSharingRole.None,
          FolderSharingRole.OnlineViewer,
          FolderSharingRole.Viewer,
          FolderSharingRole.Manager,
          FolderSharingRole.Admin,
        ].includes(role);
      }

      if (folderType === FolderType.ECONSENT) {
        return [FolderSharingRole.None, FolderSharingRole.Manager, FolderSharingRole.Admin].includes(role);
      }

      return [
        FolderSharingRole.None,
        FolderSharingRole.OnlineViewer,
        FolderSharingRole.Viewer,
        FolderSharingRole.OnlineContributor,
        FolderSharingRole.Contributor,
        FolderSharingRole.Manager,
        FolderSharingRole.Admin,
      ].includes(role);
    })
    .map((e) => ({
      label: ReadableFolderSharingRole({ role: e, folderType }),
      value: e,
      disabled:
        sharing.sharingType === FolderSharingType.External &&
        ![FolderSharingRole.None, FolderSharingRole.Viewer, FolderSharingRole.OnlineViewer].includes(e),
    }));
}

const FolderSharingTableWrapper = styled("div")`
  height: 200px;
`;
